import React from 'react';
import {
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid,
} from '@mui/material';
import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import {
  CoverageLineItemContainer,
  StyledGrid,
  StyledTextField,
} from '../styles';
import type { RateModifier } from '../../../../../api/types/rater-x-preset';

type ModalProps = {
  data: {
    ratingOptionsState: RateModifier;
    onSubmit: (vals: any) => void;
  };
  close: () => void;
};

const ScheduleRatingModal = ({ data, close }: ModalProps) => {
  const formState = useScheduledRatingForm(data.ratingOptionsState);

  function _onSubmit() {
    const totalSum = getTotalSum(formState.bucketValues);
    const buckets = Object.entries(formState.bucketValues).reduce(
      (acc, [bucket, { value }]) => {
        return {
          ...acc,
          [bucket]: {
            value,
          },
          factor: {
            value: totalSum + 1,
          },
        };
      },
      {}
    );
    data.onSubmit({
      value: {
        props: buckets,
      },
    });
    close();
  }

  return (
    <>
      <DialogContent style={{ padding: '2rem' }}>
        <ScheduleRatingInputs
          modifier={data.ratingOptionsState}
          formState={formState}
        />
      </DialogContent>
      <DialogActions>
        <CbButton
          styleName="ctaButton"
          disabled={formState.hasError}
          onClick={_onSubmit}
        >
          Save
        </CbButton>
      </DialogActions>
    </>
  );
};

export const ScheduleRatingModalConfig = {
  ScheduleRating: {
    component: ScheduleRatingModal,
    config: {
      title: 'Schedule Rating',
      fullWidth: true,
      maxWidth: 'md',
      hideCloseButton: true,
      disableEscapeKeyDown: true,
      onClose: null,
    },
  },
};

const characteristicMap = {
  antitrust: 'Antitrust',
  bankruptcy: 'Bankruptcy',
  changeInOwnership: 'Change in Ownership',
  changeInOperations: 'Change in Operations',
  futureOutlook: 'Future Outlook',
  changeOfControl: 'Change of Control',
  complexityOfRisk: 'Complexity of Risk',
  healthOfIndustry: 'Health of Industry',
  politicalRisk: 'Political Risk',
  qualityOfBoardMgmt: 'Quality of Board Management',
  regulatoryOversight: 'Regulatory Oversight',
  litigation: 'Litigation',
  policyTermsAndConditions: 'Policy Terms and Conditions',
  qualityOfMgmt: 'Quality of Management',
  sponsor: 'Sponsor',
  financialSystemsControlPractices: 'Financial Systems Control Practices',
  physicalProtectionControls: 'Physical Protection Controls',
  foreignLocations: 'Foreign Locations',
  other: 'Other',
  // P100 Pro
  vulnerabilityAndPatchManagementProgram:
    'Vulnerability and Patch Management Program',
  informationRiskManagementProgram: 'Information Risk Management Program',
  lossHistory: 'Loss History',
  industryClassification: 'Industry Classification',
  compoundingFactors: 'Compounding Factors',
};

function getTotalSum(bucketValues: Record<string, { value: number }>) {
  return Object.entries(bucketValues).reduce((acc, [mod, { value }]) => {
    if (!(mod in characteristicMap)) {
      return acc;
    }

    return acc + value;
  }, 0);
}

const ScheduleRatingInputs = ({
  modifier,
  formState,
}: {
  modifier: RateModifier;
  formState: ReturnType<typeof useScheduledRatingForm>;
}) => {
  const totalSum = React.useMemo(() => {
    return getTotalSum(formState.bucketValues);
  }, [formState.bucketValues]);

  return (
    <>
      <StyledGrid
        container
        sx={{ fontSize: '1.2rem', fontWeight: 600, padding: '0.75rem' }}
        data-testid="schedule-rating-inputs"
      >
        <Grid item xs={6}>
          Characteristics
        </Grid>
        <Grid item xs={2}>
          Credit
        </Grid>

        <Grid item xs={2}>
          Debit
        </Grid>

        <Grid item xs={2}>
          Value
        </Grid>
      </StyledGrid>

      {Object.entries(modifier.rules).map(([bucket, rule]) => {
        if (!(bucket in characteristicMap)) {
          return null;
        }

        return (
          <CoverageLineItemContainer
            container
            key={bucket}
            data-testid="characteristic"
            sx={{ fontSize: '1.2rem', fontWeight: 600, padding: '0.75rem' }}
          >
            <Grid item xs={6}>
              {characteristicMap[bucket as keyof typeof characteristicMap]}

              {formState.errors[bucket as keyof typeof formState.errors] && (
                <FormHelperText style={{ margin: 0 }} data-testid="error-text">
                  {formState.errors[bucket as keyof typeof formState.errors]}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={2}>
              ({rule.from})
            </Grid>

            <Grid item xs={2}>
              ({rule.to})
            </Grid>

            <Grid item xs={2}>
              <StyledTextField
                type="number"
                data-testid={`characteristic-input-${bucket}`}
                onChange={(e) => {
                  const value = Number(e.target.value);

                  if (value < rule.from || value > rule.to) {
                    formState.setErrors((prev) => ({
                      ...prev,
                      [bucket]: 'Value must be within the range',
                    }));
                  }

                  if (value >= rule.from && value <= rule.to) {
                    formState.setErrors((prev) => ({
                      ...prev,
                      [bucket]: '',
                    }));
                  }

                  formState.setBucketValues((prev) => ({
                    ...prev,
                    [bucket]: {
                      value,
                    },
                  }));
                }}
                inputProps={{
                  min: rule.from,
                  max: rule.to,
                  step: rule.step,
                }}
                name={bucket}
                value={formState.bucketValues[bucket]?.value ?? 0}
                sx={{
                  '& .MuiInputBase-input': {
                    height: '0.15rem',
                    textAlign: 'left',
                  },
                }}
              />
            </Grid>
          </CoverageLineItemContainer>
        );
      })}

      <CoverageLineItemContainer
        container
        sx={{
          paddingTop: '1.25rem',
          paddingBottom: '1.25rem',
          fontSize: '1.25rem',
          fontWeight: 600,
        }}
      >
        <Grid item xs={7}>
          {formState.totalError && (
            <FormHelperText style={{ margin: 0 }} data-testid="total-error">
              {formState.totalError}
            </FormHelperText>
          )}
        </Grid>
        <Grid
          item
          xs={2.5}
          sx={{ fontSize: '1.2rem', fontWeight: 600 }}
          data-testid="total-value"
        >
          Total Factor: {modifier.value.props.factor.value}
        </Grid>

        <Grid
          item
          xs={2.5}
          sx={{ fontSize: '1.2rem', fontWeight: 600 }}
          data-testid="total-value"
        >
          Total Justified: {totalSum + 1}
        </Grid>
      </CoverageLineItemContainer>
    </>
  );
};

export default ScheduleRatingInputs;

export function useScheduledRatingForm(modifier: RateModifier) {
  const [bucketValues, setBucketValues] = React.useState<
    Record<string, { value: number }>
  >(() => {
    return Object.entries(modifier.rules).reduce((acc, [bucket]) => {
      return {
        ...acc,
        [bucket]: {
          value: modifier.value.props[bucket].value,
        },
      };
    }, {});
  });
  const [errors, setErrors] = React.useState(() => {
    return Object.entries(bucketValues).reduce((acc, [bucket]) => {
      return {
        ...acc,
        [bucket]: '',
      };
    }, {});
  });

  const factorRule = modifier.rules['factor'];
  const totalJustified = getTotalSum(bucketValues) + 1;
  const hasBoundsRule = factorRule?.from != null && factorRule?.to != null;
  const isTotalOutOfBounds =
    hasBoundsRule &&
    (totalJustified < factorRule.from || totalJustified > factorRule.to);
  const totalError = isTotalOutOfBounds
    ? `Total justified must be between ${factorRule.from} and ${factorRule.to}`
    : '';

  const hasError =
    Object.values(errors).some((error) => error !== '') || totalError !== '';

  return {
    bucketValues,
    setBucketValues,
    errors,
    setErrors,
    hasError,
    totalError,
  };
}
